import { TranslateService } from '@ngx-translate/core';
import { IUser } from "../../settings/models/settings.models";
import { ITimelineEvent, TimelineEventSource, TimelineEventType } from "../models/timeline.models";
import { getUserFullname } from "./user.util";
import { classificationLabelKey, humanizeIntent, intentLabelKey } from "./classification.util";

const TIMELINE_KEYS = {
  kind: {
    [TimelineEventType.CATEGORY_CHANGE]: 'smart_inbox.timeline.kind_category',
    [TimelineEventType.INTENT_CHANGE]: 'smart_inbox.timeline.kind_intent',
    [TimelineEventType.URGENCY_CHANGE]: 'smart_inbox.timeline.kind_urgency',
    [TimelineEventType.STATUS_CHANGE]: 'smart_inbox.timeline.kind_status',
    [TimelineEventType.ASSIGNEE_CHANGE]: 'smart_inbox.timeline.kind_assignee',
    [TimelineEventType.CUSTOMER_CHANGE]: 'smart_inbox.timeline.kind_customer',
  } as Record<string, string>,
  assigned_to: 'smart_inbox.timeline.assigned_to',
  changed_to: 'smart_inbox.timeline.changed_to',
  changed_from_to: 'smart_inbox.timeline.changed_from_to',
  reply_drafted: 'smart_inbox.timeline.reply_drafted',
  automation_user: 'smart_inbox.timeline.automation_user',
  none: 'smart_inbox.timeline.none',
  email_forwarded: 'smart_inbox.timeline.email_forwarded',
  email_forward_failed: 'smart_inbox.timeline.email_forward_failed',
  order_created: 'smart_inbox.timeline.order_created',
  order_created_no_ref: 'smart_inbox.timeline.order_created_no_ref',
  order_creation_failed: 'smart_inbox.timeline.order_creation_failed',
} as const;

/**
 * Classification events carry raw backend values (`rfq`, `REQUEST_FOR_QUOTATION`,
 * `urgent`). Render them the way the rest of the UI does rather than leaking the
 * enum into the timeline.
 */
function formatEventValue(event: ITimelineEvent, value: string | null, translate: TranslateService): string | null {
  if (value === null || value === '') return null;

  switch (event.event_type) {
    case TimelineEventType.CATEGORY_CHANGE:
      return translate.instant(classificationLabelKey(value));

    case TimelineEventType.INTENT_CHANGE: {
      const key = intentLabelKey(value);
      const translated = translate.instant(key);
      return translated === key ? humanizeIntent(value) : translated;
    }

    case TimelineEventType.URGENCY_CHANGE: {
      // Unlike every other event, the backend stores this one's values already
      // translated — `_('Urgent')` / `_('Not urgent')` — resolved in the acting
      // user's language and frozen in the row. So accept the machine tokens, the
      // English source strings, and the viewer's own translations. See WB-7456:
      // once the backend emits `urgent` / `not_urgent`, the older rows still hold
      // the translated form, so this stays.
      const urgentKey = 'smart_inbox.inbox_view.detail.urgency_urgent';
      const notUrgentKey = 'smart_inbox.inbox_view.detail.urgency_not_urgent';
      const normalized = value.trim().toLowerCase();

      // Not-urgent is tested first so that switching either list to a substring
      // match later cannot make "not urgent" read as "urgent".
      const notUrgent = ['not_urgent', 'false', 'not urgent', translate.instant(notUrgentKey).toLowerCase()];
      if (notUrgent.includes(normalized)) return translate.instant(notUrgentKey);

      const urgent = ['urgent', 'true', translate.instant(urgentKey).toLowerCase()];
      if (urgent.includes(normalized)) return translate.instant(urgentKey);

      // Written in a language we are not rendering in: show it verbatim rather
      // than asserting a value that could be the opposite of the truth.
      return value;
    }

    default:
      return value;
  }
}

/**
 * Event types that describe a value being corrected rather than something being
 * attached to the thread — "changed the category to X", not "assigned category to X".
 */
const CLASSIFICATION_EVENTS: string[] = [
  TimelineEventType.CATEGORY_CHANGE,
  TimelineEventType.INTENT_CHANGE,
  TimelineEventType.URGENCY_CHANGE,
];

export function getHumanReadableTimelineEvent(event: ITimelineEvent, translate: TranslateService): string {
  const kindKey = TIMELINE_KEYS.kind[event.event_type] ?? TIMELINE_KEYS.kind[TimelineEventType.CUSTOMER_CHANGE];
  const kind = translate.instant(kindKey);
  const user = event.source === TimelineEventSource.AUTOMATION
    ? translate.instant(TIMELINE_KEYS.automation_user)
    : getUserFullname(event.user as IUser);

  // Forward events carry the recipient list in new_value and have a dedicated
  // message rather than the "changed X from A to B" shape.
  if (event.event_type === TimelineEventType.EMAIL_FORWARDED) {
    return translate.instant(TIMELINE_KEYS.email_forwarded, { user, recipients: event.new_value ?? '' });
  }
  if (event.event_type === TimelineEventType.EMAIL_FORWARD_FAILED) {
    return translate.instant(TIMELINE_KEYS.email_forward_failed, { user, recipients: event.new_value ?? '' });
  }
  // Action event (not a from→to field change): the automation drafted a reply
  // instead of auto-sending.
  if (event.event_type === TimelineEventType.REPLY_DRAFTED) {
    return translate.instant(TIMELINE_KEYS.reply_drafted, { user });
  }
  // Order events carry the order reference in new_value (absent on failure, and
  // on a success whose order was since renamed). Without a dedicated branch these
  // fall through to the generic shape and read as "assigned customer to S00417".
  if (event.event_type === TimelineEventType.ORDER_CREATED) {
    const ref = event.new_value ?? '';
    return ref
      ? translate.instant(TIMELINE_KEYS.order_created, { user, ref })
      : translate.instant(TIMELINE_KEYS.order_created_no_ref, { user });
  }
  if (event.event_type === TimelineEventType.ORDER_CREATION_FAILED) {
    return translate.instant(TIMELINE_KEYS.order_creation_failed, { user });
  }

  const formattedOld = formatEventValue(event, event.old_value, translate);
  const formattedNew = formatEventValue(event, event.new_value, translate);
  const isClassificationEvent = CLASSIFICATION_EVENTS.includes(event.event_type);

  if (formattedOld === null && formattedNew !== null && event.event_type !== TimelineEventType.STATUS_CHANGE) {
    // Setting a category/intent/urgency for the first time is still a change,
    // not an assignment — only assignee/customer read as "assigned to".
    const key = isClassificationEvent ? TIMELINE_KEYS.changed_to : TIMELINE_KEYS.assigned_to;
    return translate.instant(key, { user, kind, value: formattedNew, newValue: formattedNew });
  }

  let oldValue = formattedOld ?? translate.instant(TIMELINE_KEYS.none);
  let newValue = formattedNew ?? translate.instant(TIMELINE_KEYS.none);

  // If the status returned by the API is null, it means the status has been set to "New".
  if (event.event_type === TimelineEventType.STATUS_CHANGE) {
    if(!event.old_value) {
      oldValue = translate.instant('smart_inbox.inbox_view.detail.status_new');
    }

    if(!event.new_value) {
      newValue = translate.instant('smart_inbox.inbox_view.detail.status_new');
    }
  }

  return translate.instant(TIMELINE_KEYS.changed_from_to, { user, kind, oldValue, newValue });
}

export function getIconForTimelineEvent(event: ITimelineEvent): string {
    if (event.source === TimelineEventSource.AUTOMATION) {
        return 'bolt';
    }

    switch (event.event_type) {
        case TimelineEventType.CATEGORY_CHANGE: return 'tag-cc';
        case TimelineEventType.INTENT_CHANGE: return 'tag-cc';
        case TimelineEventType.URGENCY_CHANGE: return 'flag-filled';
        case TimelineEventType.STATUS_CHANGE: return 'timeline_event';
        case TimelineEventType.ASSIGNEE_CHANGE: return 'user_icon';
        case TimelineEventType.CUSTOMER_CHANGE: return 'company-cc';
        default: return 'timeline_event';
    }
}
